import { rem } from 'polished'
import { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import DeleteConfirmation from './DeleteConfirmation'

import NestableClickable from 'components/Common/NestableClickable'
import Group from 'components/utils/Group'
import ModalContext from 'contexts/ModalContext'
import LinePaperclipIcon from 'LuxKit/Icons/line/LinePaperclipIcon'
import LinePenIcon from 'LuxKit/Icons/line/LinePenIcon'
import LineTrashAltIcon from 'LuxKit/Icons/line/LineTrashAltIcon'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import ModalBase from 'LuxKit/Modal/ModalBase'
import Caption from 'LuxKit/Typography/Caption'
import focusStyle from 'styles/utils/focusStyle'
import RenameDocumentModal from 'tripPlanner/components/Documents/RenameDocumentModal'
import TripContext from 'tripPlanner/contexts/TripContext'
import useViewDocument from 'tripPlanner/hooks/useViewDocument'
import type { TripDocument } from 'tripPlanner/types/documents'
import type { TripItem } from 'tripPlanner/types/tripItem'
import OptionsButton from './OptionsButton'

const FullWidthClickable = styled(NestableClickable)`
  width: fit-content;

  &:focus-visible {
    ${focusStyle}
  }
`

const Root = styled(Group)`
  padding: ${rem(4)} ${rem(8)};
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  border-radius: ${props => props.theme.borderRadius.M};

  &:hover {
    background-color: ${props => props.theme.palette.neutral.default.seven};
  }
`

const Filename = styled(Caption)`
  flex: 1;
`

interface Props {
  document: TripDocument
  item: TripItem
}

function AttachedDocument({ document, item }: Props) {
  const intl = useIntl()
  const trip = useContext(TripContext)
  const showModal = useContext(ModalContext)
  const tripId = trip?.tripId
  const canEdit = trip?.permissions.canEdit ?? false
  const documentWithItemInfo = useMemo(() => ({ ...document, item: { id: item.id, name: item.sortableName } }), [document, item])

  const showDocument = useViewDocument(document)

  const handleRename = useCallback(
    () => {
      if (!tripId) return

      showModal(
        <ModalBase>
          <RenameDocumentModal
            tripId={tripId}
            document={documentWithItemInfo}
          />
        </ModalBase>,
      )
    },
    [showModal, tripId, documentWithItemInfo],
  )

  const handleDelete = useCallback(
    () => {
      if (!tripId) return

      showModal(
        <ModalBase>
          <DeleteConfirmation tripId={tripId} documentKey={document.key} />
        </ModalBase>,
      )
    },
    [showModal, tripId, document.key],
  )

  return (
    <FullWidthClickable onClick={showDocument}>
      <Root direction="horizontal" gap={12} verticalAlign="center" fullWidth>
        <LinePaperclipIcon size="XS" colour="neutral-two" />
        <Filename
          variant="medium"
          weight="bold"
          wrap="letter-wrap"
          colour="neutral-two"
          lineClamp={1}
        >
          {document.filename}
        </Filename>
        {canEdit && (
          <OptionsButton
            title={intl.formatMessage({
              id: '-fICq2cc0xCkIITdLgoC',
              defaultMessage: 'Menu',
              description: 'Accessible title for attached document options menu button',
            })}
            size="xsmall"
            data-menu-button
            bodyContent={
              <>
                <ClickableListItem
                  startIcon={<LinePenIcon size="S" />}
                  title={intl.formatMessage({
                    id: 'TcX3E0yRWtjefjuTGQEN',
                    defaultMessage: 'Rename',
                    description: 'Menu option to rename an attached document in the trip planner documents section',
                  })}
                  onClick={handleRename}
                />
                <ClickableListItem
                  startIcon={<LineTrashAltIcon size="S" />}
                  title={intl.formatMessage({
                    id: 'w88prYZRoV2rWgj8o-Zd',
                    defaultMessage: 'Delete',
                    description: 'Menu option to delete an attached document in the trip planner documents section',
                  })}
                  onClick={handleDelete}
                />
              </>
            }
          />
        )}
      </Root>
    </FullWidthClickable>
  )
}

export default AttachedDocument
